import React, { useEffect, useState } from 'react';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
  type DragStartEvent,
  type DragOverEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable';
import type { PartOrderLine } from '@techgitter/contracts';
import type { WorkItem } from '../workitems/workItemTypes';
import { TechnicianCard } from './TechnicianCard';
import { JobRow } from './JobRow';
import { AppointmentRow } from './AppointmentRow';
import { CheckInRow } from './CheckInRow';
import { SortableRow } from './SortableRow';
import {
  initialsFromName,
  isAppointmentItem,
  isCheckInItem,
  toJobRowProps,
  toAppointmentRowProps,
  toCheckInRowProps,
} from './workItemToRow';
import { useUserColors } from '../../hooks/useUserColors';
import { useUsers } from '../../hooks/useUsers';
import { ReassignMenu } from './ReassignMenu';
import type { AvatarColour } from '../../styles/avatarPalette';

/** Context carried by each draggable item. */
export interface DragData {
  type: 'job' | 'appointment';
  jobId: string;
  technicianId: string;
  index: number;
}

export interface TechnicianCardListProps {
  /** Assignee keys in display order. */
  assignees: string[];
  /** Work items grouped by assignee, each already sorted for display. */
  groups: Record<string, WorkItem[]>;
  /** Currently-collapsed assignee groups. */
  collapsed: Set<string>;
  onToggle: (assignee: string) => void;
  onRefresh: () => void;
  onResolvedToday: () => void;
  /**
   * Commit a move. `targetId` is the guid the source should be inserted BEFORE,
   * or `end::{assignee}` to drop at the bottom of a group — matching the
   * dashboard's existing reorder/reassign contract.
   */
  onMove: (sourceGuid: string, targetId: string) => void;
  /** Reassign a work item to a chosen technician (from the handle context menu). */
  onReassign?: (sourceGuid: string, assigneeFullName: string, assigneeEmail?: string) => void;
  onEditItem?: (item: WorkItem) => void;
  /** Convert a standalone appointment into a job (gated to appointment rows). */
  onConvertToJob?: (item: WorkItem) => void;
  /** Delete a job (from its "···" overflow menu, via an inline confirmation). */
  onDeleteItem?: (item: WorkItem) => Promise<void>;
  /** Renders the per-row expansion panel shown when a row's chevron is toggled. */
  renderExpansion?: (item: WorkItem) => React.ReactNode;
  /** Resolves part-order lines for a row's parts-ready badge. */
  getPartLines?: (item: WorkItem) => PartOrderLine[] | undefined;
  /** Change a job's status from its status pill. */
  onStatusChange?: (item: WorkItem, dvValue: string) => void;
}

/** Compute the insert-before target for a guid within an ordered array. */
function targetForIndex(arr: WorkItem[], guid: string, assignee: string): string {
  const idx = arr.findIndex((w) => w.guid === guid);
  if (idx >= 0 && idx < arr.length - 1) return arr[idx + 1].guid;
  return `end::${assignee}`;
}

/** One technician group: a droppable card (droppable even when collapsed). */
const TechGroup: React.FC<{
  assignee: string;
  avatarColor?: AvatarColour;
  items: WorkItem[];
  collapsed: boolean;
  onToggle: (a: string) => void;
  onRefresh: () => void;
  onResolvedToday: () => void;
  onEditItem?: (item: WorkItem) => void;
  onConvertToJob?: (item: WorkItem) => void;
  onDeleteItem?: (item: WorkItem) => Promise<void>;
  renderExpansion?: (item: WorkItem) => React.ReactNode;
  getPartLines?: (item: WorkItem) => PartOrderLine[] | undefined;
  onStatusChange?: (item: WorkItem, dvValue: string) => void;
  onReassignRequest?: (item: WorkItem, position: { top: number; left: number }) => void;
}> = ({ assignee, avatarColor, items, collapsed, onToggle, onRefresh, onResolvedToday, onEditItem, onConvertToJob, onDeleteItem, renderExpansion, getPartLines, onStatusChange, onReassignRequest }) => {
  // The whole card is the droppable container (id === assignee) so cross-tech
  // drops land even when the group is collapsed / empty.
  const { setNodeRef } = useDroppable({ id: assignee });
  return (
    <div ref={setNodeRef}>
      <TechnicianCard
        initials={initialsFromName(assignee)}
        avatarColor={avatarColor}
        name={assignee}
        count={items.length}
        collapsed={collapsed}
        onToggle={() => onToggle(assignee)}
        onRefresh={onRefresh}
        onResolvedToday={onResolvedToday}
      >
        <SortableContext items={items.map((w) => w.guid)} strategy={verticalListSortingStrategy}>
          {items.map((item, index) => (
            <SortableRow
              key={item.guid}
              item={item}
              assignee={assignee}
              index={index}
              onEditItem={onEditItem}
              onConvertToJob={onConvertToJob}
              onDeleteItem={onDeleteItem}
              renderExpansion={renderExpansion}
              partLines={getPartLines?.(item)}
              onStatusChange={onStatusChange}
              onReassignRequest={onReassignRequest}
            />
          ))}
        </SortableContext>
      </TechnicianCard>
    </div>
  );
};

/**
 * Drag-and-drop root for the V2 technician dashboard. Maintains a local mirror
 * of the grouped items for live cross-technician movement, then commits the
 * final position via `onMove` (which persists through the real reorder/reassign
 * APIs and rolls back on failure, re-syncing this mirror from `groups`).
 */
export const TechnicianCardList: React.FC<TechnicianCardListProps> = ({
  assignees,
  groups,
  collapsed,
  onToggle,
  onRefresh,
  onResolvedToday,
  onMove,
  onReassign,
  onEditItem,
  onConvertToJob,
  onDeleteItem,
  renderExpansion,
  getPartLines,
  onStatusChange,
}) => {
  const [local, setLocal] = useState<Record<string, WorkItem[]>>(groups);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [reassignTarget, setReassignTarget] = useState<
    { item: WorkItem; anchor: { top: number; left: number } } | null
  >(null);
  const { colorFor } = useUserColors();
  const { users: allUsers } = useUsers();

  const onReassignRequest = onReassign
    ? (item: WorkItem, position: { top: number; left: number }) =>
        setReassignTarget({ item, anchor: position })
    : undefined;

  // Re-sync from props whenever the source data changes and no drag is active.
  useEffect(() => {
    if (!activeId) setLocal(groups);
  }, [groups, activeId]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
  );

  const findContainer = (id: string): string | undefined => {
    if (id in local) return id;
    return assignees.find((a) => local[a]?.some((w) => w.guid === id));
  };

  const activeItem = activeId
    ? Object.values(local).flat().find((w) => w.guid === activeId) ?? null
    : null;

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;
    if (!over) return;
    const activeContainer = findContainer(String(active.id));
    const overContainer = findContainer(String(over.id));
    if (!activeContainer || !overContainer || activeContainer === overContainer) return;
    // Reassignment into "Unassigned" isn't supported by the API — don't move it
    // there visually either.
    if (overContainer === 'Unassigned') return;

    setLocal((prev) => {
      const activeItems = prev[activeContainer] ?? [];
      const overItems = prev[overContainer] ?? [];
      const activeIndex = activeItems.findIndex((w) => w.guid === active.id);
      if (activeIndex < 0) return prev;
      const moved = activeItems[activeIndex];
      let overIndex = overItems.findIndex((w) => w.guid === over.id);
      if (overIndex < 0) overIndex = overItems.length; // dropped on the container itself
      return {
        ...prev,
        [activeContainer]: activeItems.filter((w) => w.guid !== active.id),
        [overContainer]: [
          ...overItems.slice(0, overIndex),
          moved,
          ...overItems.slice(overIndex),
        ],
      };
    });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    const sourceGuid = String(active.id);
    setActiveId(null);

    const finalContainer = findContainer(sourceGuid);
    if (!over || !finalContainer) {
      setLocal(groups); // nothing valid — restore
      return;
    }

    // Reorder within the final container (if dropped onto a sibling row).
    let finalArray = local[finalContainer] ?? [];
    const overContainer = findContainer(String(over.id));
    if (overContainer === finalContainer && over.id !== active.id) {
      const oldIndex = finalArray.findIndex((w) => w.guid === sourceGuid);
      const newIndex = finalArray.findIndex((w) => w.guid === over.id);
      if (oldIndex >= 0 && newIndex >= 0) {
        finalArray = arrayMove(finalArray, oldIndex, newIndex);
        setLocal((prev) => ({ ...prev, [finalContainer]: finalArray }));
      }
    }

    const targetId = targetForIndex(finalArray, sourceGuid, finalContainer);

    // No-op guard: same container and same insert-before neighbour as before.
    const origContainer = assignees.find((a) => groups[a]?.some((w) => w.guid === sourceGuid));
    if (origContainer) {
      const origTarget = targetForIndex(groups[origContainer], sourceGuid, origContainer);
      if (origContainer === finalContainer && origTarget === targetId) {
        setLocal(groups);
        return;
      }
    }

    onMove(sourceGuid, targetId);
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {assignees.map((assignee) => (
          <TechGroup
            key={assignee}
            assignee={assignee}
            avatarColor={assignee === 'Unassigned' ? undefined : colorFor({ name: assignee })}
            items={local[assignee] ?? []}
            collapsed={collapsed.has(assignee)}
            onToggle={onToggle}
            onRefresh={onRefresh}
            onResolvedToday={onResolvedToday}
            onEditItem={onEditItem}
            onConvertToJob={onConvertToJob}
            onDeleteItem={onDeleteItem}
            renderExpansion={renderExpansion}
            getPartLines={getPartLines}
            onStatusChange={onStatusChange}
            onReassignRequest={onReassignRequest}
          />
        ))}
      </div>
      <DragOverlay>
        {activeItem
          ? (isCheckInItem(activeItem)
              ? <CheckInRow {...toCheckInRowProps(activeItem)} />
              : isAppointmentItem(activeItem)
              ? <AppointmentRow {...toAppointmentRowProps(activeItem)} />
              : <JobRow {...toJobRowProps(activeItem)} />)
          : null}
      </DragOverlay>
      {onReassign && (
        <ReassignMenu
          anchorPosition={reassignTarget?.anchor ?? null}
          currentAssignee={reassignTarget ? (reassignTarget.item.userFullName || 'Unassigned') : ''}
          assigneesWithTickets={assignees.filter((a) => a !== 'Unassigned')}
          allUsers={allUsers}
          onReassign={(fullName, email) => {
            if (reassignTarget) onReassign(reassignTarget.item.guid, fullName, email);
          }}
          onClose={() => setReassignTarget(null)}
        />
      )}
    </DndContext>
  );
};
